import { CommonModule } from '@angular/common';
import { NgModule } from '@angular/core';
import { RouterModule } from '@angular/router';

import { AutomationModule } from '@frontend/sports/automation/core-feature';
import { FontResizerComponent } from '@frontend/sports/common/core/feature/font-resizer';
import { ScrollAdapterComponent } from '@frontend/sports/common/core/feature/scroll-adapter';
import { ScrollSelectedInViewDirective } from '@frontend/sports/common/core/feature/scroll-selected-in-view';
import { ShowInEpcotDirective } from '@frontend/sports/common/core/feature/show-in-epcot';
import { StringInterpolationPipe } from '@frontend/sports/common/core/utils/string-interpolation';
import { ColoredIconComponent } from '@frontend/sports/common/ui/colored-icon';
import { SportsThemedButtonDirective } from '@frontend/sports/common/ui/sports-themed-button';
import { CompetitionLogoComponent } from '@frontend/sports/competition-logos/feature';
import { FavouriteEventToggleComponent } from '@frontend/sports/favourites/feature/event-toggle';
import { GestureModule } from '@frontend/sports/gesture/feature';
import { GridBaseModule } from '@frontend/sports/grid/core/feature/store';
import { NativePriceFormatterPipe } from '@frontend/sports/odds/feature/native-price';
import { ParticipantImageComponent } from '@frontend/sports/participant-image/feature';
import { PriceboostCountSignpostComponent } from '@frontend/sports/price-boost/feature';
import { TooltipModule } from '@frontend/sports/tooltip-feature';
import { PerformanceTrackerComponent } from '@frontend/sports/tracking/feature/performance-tracker';
import { NavigationCoreModule } from '@frontend/sports/web/navigation-lib';
import { DsButton } from '@frontend/ui/button';
import { DsButtonIcon } from '@frontend/ui/button-icon';
import { DsPill } from '@frontend/ui/pill';
import { DsRadioModule } from '@frontend/ui/radio-button';
import { DsSegmentedControlModule } from '@frontend/ui/segmented-control';
import { DsTouchClickDirective } from '@frontend/ui/shared';
import { SpeculativeLinkDirective } from '@frontend/vanilla/features/speculative-link';
import { IconCustomComponent } from '@frontend/vanilla/shared/icons';
import { EffectsModule } from '@ngrx/effects';
import { RxFor } from '@rx-angular/template/for';
import { NgxPopperjsModule } from 'ngx-popperjs';

import { BetBuilderOverlayHeaderComponent } from '../bet-builder-core/components/bet-builder-overlay-header/bet-builder-overlay-header.component';
import { TranslationToStringPipe } from '../cds/translation.pipe';
import { ErrorComponent } from '../common/error/error.component';
import { BadgeComponent } from '../crm-promotion/badge.component';
import { CrmPromotionModule } from '../crm-promotion/crm-promotion.module';
import { DropdownModule } from '../dropdown/dropdown.module';
import { GridScoreboardModule } from '../grid-scoreboard/grid-scoreboard.module';
import { NativeAlertsModule } from '../native-alerts/native-alerts.module';
import { OptionPanelComponent } from '../option-group/option-panel.component';
import { EventPickComponent } from '../option-pick/event-pick.component';
import { OptionSheetViewModule } from '../option-sheet-view/option-sheet-view.module';
import { TimerModule } from '../timers/timer.module';
import { CompetitionGridTrackingEffects } from './competition-grid-tracking.epic';
import { CompetitionGridEffects } from './competition-grid.epic';
import { CompactEventComponent } from './content/compact-event.component';
import { EventDetailComponent } from './content/event-detail.component';
import { EventFooterComponent } from './content/event-footer.component';
import { EventInfoComponent } from './content/event-info.component';
import { EventMediaComponent } from './content/event-media.component';
import { EventNameComponent } from './content/event-name.component';
import { EventComponent } from './content/event.component';
import { FixtureFormComponent } from './content/form-indicator/fixture-form.component';
import { FormIndicatorComponent } from './content/form-indicator/form-indicator.component';
import { GameNotesComponent } from './content/game-notes.component';
import { OptionGroupComponent } from './content/option-group/option-group.component';
import { OptionComponent } from './content/option-group/option.component';
import { RaceInfoComponent } from './content/race-info.component';
import { SixPackEventComponent } from './content/six-pack-event.component';
import { CouponGridEffects } from './coupon-grid.epic';
import { GridListComponent } from './grid-list.component';
import { GridOptionGroupComponent } from './grid-option-group.component';
import { GridComponent } from './grid.component';
import { EventGroupComponent } from './header/event-group.component';
import { EventHeaderComponent } from './header/event-header.component';
import { GridHeaderComponent } from './header/grid-header.component';
import { GridPillsHeaderComponent } from './header/grid-pills-header.component';
import { GroupHeaderComponent } from './header/group-header.component';
import { GroupSelectorComponent } from './header/group-selector.component';
import { SortSelectorComponent } from './header/sort-selector.component';

@NgModule({
    imports: [
        EffectsModule.forFeature([CouponGridEffects, CompetitionGridTrackingEffects, CompetitionGridEffects]),
        AutomationModule,
        TranslationToStringPipe,
        CommonModule,
        CrmPromotionModule,
        DropdownModule,
        GestureModule,
        NativeAlertsModule,
        NavigationCoreModule,
        EventPickComponent,
        TimerModule,
        TooltipModule,
        NgxPopperjsModule,
        ParticipantImageComponent,
        CompetitionLogoComponent,
        BetBuilderOverlayHeaderComponent,
        GridScoreboardModule,
        PriceboostCountSignpostComponent,
        GridBaseModule,
        PerformanceTrackerComponent,
        ColoredIconComponent,
        ErrorComponent,
        ShowInEpcotDirective,
        FontResizerComponent,
        StringInterpolationPipe,
        BadgeComponent,
        RxFor,
        OptionPanelComponent,
        DsButton,
        RouterModule.forChild([]),
        SportsThemedButtonDirective,
        DsPill,
        ScrollAdapterComponent,
        ScrollSelectedInViewDirective,
        IconCustomComponent,
        DsButtonIcon,
        SpeculativeLinkDirective,
        OptionSheetViewModule,
        DsSegmentedControlModule,
        NativePriceFormatterPipe,
        DsTouchClickDirective,
        DsRadioModule,
        EventDetailComponent,
        EventInfoComponent,
        EventMediaComponent,
        EventNameComponent,
        FixtureFormComponent,
        FormIndicatorComponent,
        RaceInfoComponent,
        GameNotesComponent,
        GridOptionGroupComponent,
        EventHeaderComponent,
        OptionGroupComponent,
        OptionComponent,
        SortSelectorComponent,
        EventGroupComponent,
        GroupHeaderComponent,
        FavouriteEventToggleComponent,
    ],
    declarations: [
        CompactEventComponent,
        EventComponent,
        GridComponent,
        GridHeaderComponent,
        GridPillsHeaderComponent,
        GridListComponent,
        GroupSelectorComponent,
        SixPackEventComponent,
        EventFooterComponent,
    ],
    exports: [
        EventDetailComponent,
        EventNameComponent,
        TimerModule,
        OptionGroupComponent,
        EventMediaComponent,
        OptionComponent,
        GridComponent,
        GridListComponent,
        EventFooterComponent,
        EventInfoComponent,
        RaceInfoComponent,
        GameNotesComponent,
        FixtureFormComponent,
        GroupSelectorComponent,
        GridOptionGroupComponent,
        EventHeaderComponent,
        GridPillsHeaderComponent,
        GridHeaderComponent,
    ],
})
export class GridModule {}
